import { parse } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

import { FilterValuesObj, getActiveFilters } from '@/components/PatientDataTable';
import {
	FetchHciListsRequest,
	FetchHciListsResponseObject,
} from '@/features/healthCareInstitution';
import { baseApi } from '@/lib/rtkQuery/baseApi';

import {
	FetchAllPatientListsRequest,
	CreatePatientListRequest,
	CreatePatientResponseObject,
	CriteriaObject,
	FetchListPatientsRequest,
	FetchListPatientsResponseObject,
	FetchListDetailsResponseObject,
	FetchPatientListRequest,
	FetchPatientListResponseObject,
	FetchSearchCriteriaListResponseObject,
	FetchSurveyListResponseObject,
	SearchCriteriaObject,
	FetchAllPatientListsResponse,
	SendSurveyResponseObj,
	SendSurveyRequest,
	AssignPatientToStudyResponse,
	AssignPatientToStudyRequest,
	SendDataSharingLinkRequest,
	SendDataSharingLinkResponse,
	FetchListPatientsIdsResponseObject,
	RemoveNewRequest,
} from '../types';
import { formatListResponse, formatFormData } from '../utils';

export const patientListApi = baseApi
	.enhanceEndpoints({
		addTagTypes: ['patient_list', 'all_patient_list', 'hci_lists', 'list_details'],
	})
	.injectEndpoints({
		endpoints: (build) => ({
			fetchPatientList: build.query<FetchPatientListResponseObject, FetchPatientListRequest>({
				query: ({ search_value, page_size, page, filtersForm }) => {
					const searchValue = search_value ? search_value : '';
					const filters = formatFormData(filtersForm);
					return {
						url: 'v1/patient-search/',
						method: 'POST',
						body: {
							page,
							page_size,
							filters: {
								...filters,
								search: searchValue,
							},
						},
					};
				},
				transformResponse: (response: FetchPatientListResponseObject) => {
					return {
						...response,
						results: response.results.map((result) => {
							return {
								...result,
								search_criteria: [
									...new Set(
										(result.search_criteria as SearchCriteriaObject[])
											.map((item: SearchCriteriaObject) => {
												return item.criteria.filter(
													(item: CriteriaObject) => item.name?.length > 0
												);
											})
											.flat()
									),
								],
							};
						}),
					};
				},
				providesTags: ['patient_list'],
			}),
			createPatientList: build.mutation<
				CreatePatientResponseObject,
				CreatePatientListRequest
			>({
				query: (data) => {
					const activeFilters = getActiveFilters(data.filters as FilterValuesObj, [
						'survey_filters',
						'contact_filters',
					]);
					const searchValue = data.search_value ? data.search_value : '';
					const filters = formatFormData(activeFilters);
					return {
						url: 'v1/patient-list/',
						method: 'POST',
						body: {
							name: data.name,
							list_type: data.list_type,
							filters: {
								...filters,
								search: searchValue,
							},
						},
					};
				},
				invalidatesTags: ['all_patient_list'],
			}),
			fetchSearchCriteria: build.query<FetchSearchCriteriaListResponseObject, void>({
				query: () => {
					return {
						url: `v1/search-criteria/?ordering=name&page=1&page_size=500`,
					};
				},
				transformResponse: (response: FetchSearchCriteriaListResponseObject) => {
					return Array.isArray(response)
						? { results: response, total: response.length }
						: response;
				},
			}),
			fetchAllSurveyList: build.query<FetchSurveyListResponseObject, void>({
				query: () => {
					return {
						url: `v1/study/survey-version/?all=true`,
					};
				},
				transformResponse: (response: FetchSurveyListResponseObject) => {
					return Array.isArray(response)
						? { results: response, total: response.length }
						: response;
				},
			}),
			fetchLatestVersionSurveyList: build.query<FetchSurveyListResponseObject, void>({
				query: () => {
					return {
						url: `v1/study/survey-version/?all=true&is_active=true&latest_version_only=true`,
					};
				},
				transformResponse: (response: FetchSurveyListResponseObject) => {
					return Array.isArray(response)
						? { results: response, total: response.length }
						: response;
				},
			}),
			fetchAllPatientLists: build.query<
				FetchAllPatientListsResponse,
				FetchAllPatientListsRequest
			>({
				query: ({ page, page_size, ordering, search_value, archived = false }) => {
					const encoded_search_value = encodeURIComponent(
						search_value ? search_value : ''
					);
					return {
						url: `v1/patient-list/?page=${page}&page_size=${page_size}&ordering=${ordering}&search=${encoded_search_value}&archived=${archived}`,
						method: 'GET',
					};
				},
				providesTags: ['all_patient_list'],
			}),
			fetchListDetails: build.query<FetchListDetailsResponseObject, string>({
				query: (list_identifier) => {
					return {
						url: `v1/patient-list/${list_identifier}/`,
					};
				},
				transformResponse: (response: FetchListDetailsResponseObject) =>
					formatListResponse(response),
				providesTags: ['list_details'],
			}),
			fetchListPatients: build.query<
				FetchListPatientsResponseObject,
				FetchListPatientsRequest
			>({
				query: ({ id, search_value, page, page_size, ordering, membership_status }) => {
					const encoded_search_value = encodeURIComponent(
						search_value ? search_value : ''
					);
					return {
						url: `v1/patient-list/${id}/patients/?search=${encoded_search_value}&page=${page}&page_size=${page_size}${ordering && ordering.length > 0 ? `&ordering=${ordering}` : ''}&membership_status=${membership_status}`,
					};
				},
				transformResponse: (response: FetchListPatientsResponseObject) => {
					return {
						...response,
						results: response.results.map((result) => {
							return {
								...result,
								search_criteria: [
									...new Set(
										(result.search_criteria as SearchCriteriaObject[])
											.map((item: SearchCriteriaObject) => {
												return item.criteria.filter(
													(item: CriteriaObject) => item.name?.length > 0
												);
											})
											.flat()
									),
								],
							};
						}),
					};
				},
				providesTags: ['patient_list'],
			}),
			fetchListPatientsIds: build.query<
				FetchListPatientsIdsResponseObject,
				FetchListPatientsRequest
			>({
				query: ({ id, search_value, membership_status }) => {
					const encoded_search_value = encodeURIComponent(
						search_value ? search_value : ''
					);
					return {
						url: `v1/patient-list/${id}/patients/ids/?search=${encoded_search_value}&membership_status=${membership_status}`,
					};
				},
				providesTags: ['patient_list'],
			}),
			sendSurvey: build.mutation<SendSurveyResponseObj, SendSurveyRequest>({
				query: ({ values, patientIds, list_id }) => {
					// convert date/time values to Date object (in local time)
					const dateFormatInput = 'y-MM-dd HH:mm:ss'; // YYYY-MM-DD HH:MM:SS
					const sendAtDateTime = parse(
						`${values.send_date} ${values.send_time}`,
						dateFormatInput,
						new Date()
					);
					// convert date/time to UTC time string
					const dateFormatOutput = `yyyy-MM-dd'T'HH:mm:ss'Z'`; //e.g. 2024-06-25T07:43:48Z
					const utcDateTimeStr = formatInTimeZone(
						sendAtDateTime,
						'UTC',
						dateFormatOutput
					);
					// only include patientIds in the request body if they are provided (not null)
					return {
						url: `v1/patient-list/${list_id}/send-survey/`,
						method: 'POST',
						body: {
							survey_version: values.survey_version,
							send_at: utcDateTimeStr,
							...(patientIds !== null ? { patient_ids: patientIds } : {}),
						},
					};
				},
			}),
			archiveList: build.mutation<void, string>({
				query: (list_identifier) => {
					return {
						url: `v1/patient-list/${list_identifier}/`,
						method: 'PATCH',
						body: {
							archived: true,
						},
					};
				},
				invalidatesTags: ['all_patient_list', 'list_details'],
			}),
			fetchHciListsFilter: build.query<FetchHciListsResponseObject, FetchHciListsRequest>({
				query: () => {
					return {
						url: `v1/healthcare-provider/provider?all=true`,
						method: 'GET',
					};
				},
				transformResponse: (response: FetchHciListsResponseObject) => {
					return Array.isArray(response)
						? { results: response, total: response.length }
						: response;
				},
				providesTags: ['hci_lists'],
			}),
			fetchAllListsFilter: build.query<
				FetchAllPatientListsResponse,
				FetchAllPatientListsRequest
			>({
				query: () => {
					return {
						url: `v1/patient-list/?all=true&archived=false`,
						method: 'GET',
					};
				},
				transformResponse: (response: FetchAllPatientListsResponse) => {
					return Array.isArray(response)
						? { results: response, total: response.length }
						: response;
				},
				providesTags: ['all_patient_list'],
			}),
			assignPatientsToStudy: build.mutation<
				AssignPatientToStudyResponse,
				AssignPatientToStudyRequest
			>({
				query: ({ list_identifier, study_identifier, patient_status, patientIds }) => {
					// only include patientIds in the request body if they are provided (not null)
					return {
						url: `v1/patient-list/${list_identifier}/assign-study/`,
						method: 'POST',
						body: {
							study: study_identifier,
							status: patient_status,
							...(patientIds !== null ? { patient_ids: patientIds } : {}),
						},
					};
				},
				invalidatesTags: ['patient_list'],
			}),
			sendDataSharingLink: build.mutation<
				SendDataSharingLinkResponse,
				SendDataSharingLinkRequest
			>({
				query: ({ list_identifier, patientIds }) => {
					return {
						url: `v1/patient-list/${list_identifier}/data-share/`,
						method: 'POST',
						body: {
							...(patientIds !== null ? { patient_ids: patientIds } : {}),
						},
					};
				},
				invalidatesTags: ['patient_list'],
			}),
			removeNew: build.mutation<null, RemoveNewRequest>({
				query: ({ list_identifier, patientIds }) => {
					return {
						url: `v1/patient-list/${list_identifier}/patients/remove-new/`,
						method: 'POST',
						body: {
							patient_ids: patientIds,
						},
					};
				},
				invalidatesTags: ['patient_list', 'all_patient_list', 'list_details'],
			}),
		}),
	});

export const {
	useFetchPatientListQuery,
	useCreatePatientListMutation,
	useFetchSearchCriteriaQuery,
	useFetchAllSurveyListQuery,
	useFetchLatestVersionSurveyListQuery,
	useFetchAllPatientListsQuery,
	useFetchListDetailsQuery,
	useFetchListPatientsQuery,
	useFetchListPatientsIdsQuery,
	useSendSurveyMutation,
	useArchiveListMutation,
	useFetchHciListsFilterQuery,
	useFetchAllListsFilterQuery,
	useAssignPatientsToStudyMutation,
	useSendDataSharingLinkMutation,
	useRemoveNewMutation,
} = patientListApi;
